import { heapAddEventProperties, heapTrackProperties } from '@m/utils/heap';

import { getCallingCode, getPhoneNumberCountry, isInternationalCallingCode } from './phoneCountry';
import { getVerificationErrorReason } from './phoneVerificationErrors';

import type { Country } from 'react-phone-number-input';

/**
 * Heap tracking for the phone-entry and verify-phone steps of every flow, built for one set of
 * dashboards. Every event is `PhoneVerification:*` and carries the same base properties
 * (`source`, `country`, `callingCode`, `isInternational`, `isPhoneChange`), so a funnel can be
 * split by screen, country or international vs +1 without per-flow events:
 *
 *   PhoneStep:Viewed → (PhoneStep:CountryChanged) → (Captcha:*) → CodeRequested
 *     → VerifyStep:Viewed → (ResendClicked → Captcha:* → CodeRequested) → CodeSubmitted
 *
 * Never sends the phone number itself, only its country.
 */

/** The screen the step belongs to. */
export type PhoneVerificationSource =
  | 'ChangePhone'
  | 'ExpressOnboarding'
  | 'LegacyOnboarding'
  | 'Onboarding'
  | 'PhysicalCardSmsOptIn'
  | 'RecognitionEnrollment'
  | 'ReservationCheckout'
  | 'ReservationReverify'
  | 'SignInSheet';

/** Whether the captcha guards the first send or a resend. */
export type CaptchaTrigger = 'Resend' | 'Send';

export type StepContext = {
  /** A signed-in user verifying a new number (update-phone endpoints), not signing in. */
  isPhoneChange?: boolean;
  source: PhoneVerificationSource;
};

type CaptchaContext = {
  country?: Country;
  source: PhoneVerificationSource;
  trigger: CaptchaTrigger;
};

const countryProps = (country: Country | undefined) => {
  const callingCode = getCallingCode(country);
  return {
    callingCode,
    country,
    isInternational: callingCode ? isInternationalCallingCode(callingCode) : undefined,
  };
};

const stepProps = ({ isPhoneChange = false, source }: StepContext, country?: Country) => ({
  ...countryProps(country),
  isPhoneChange,
  source,
});

const captchaProps = ({ country, source, trigger }: CaptchaContext) => ({
  ...countryProps(country),
  source,
  trigger,
});

const secondsSince = (timestamp: number | undefined) =>
  timestamp ? Math.round((Date.now() - timestamp) / 1000) : undefined;

/**
 * Attempt counters for the number currently being verified, so events can say "3rd resend" or
 * "2nd code entry" and how long after the code was sent. Kept in memory only, never sent.
 */
let attempt: {
  codeSentAt?: number;
  phoneNumber: string;
  requests: number;
  resends: number;
  verifications: number;
} | null = null;

const attemptFor = (phoneNumber: string) => {
  if (attempt?.phoneNumber !== phoneNumber) {
    attempt = { phoneNumber, requests: 0, resends: 0, verifications: 0 };
  }
  return attempt;
};

export const trackPhoneStepViewed = (context: StepContext & { country?: Country }) => {
  heapTrackProperties('PhoneVerification:PhoneStep:Viewed', stepProps(context, context.country));
};

export const trackPhoneCountryChanged = (
  context: StepContext & { country?: Country; fromCountry?: Country },
) => {
  heapTrackProperties('PhoneVerification:PhoneStep:CountryChanged', {
    ...stepProps(context, context.country),
    fromCountry: context.fromCountry,
  });
};

export const trackVerificationCodeRequested = ({
  durationMs,
  error,
  hasHcaptchaToken,
  isResend,
  phoneNumber,
  success,
  ...context
}: StepContext & {
  durationMs?: number;
  error?: string;
  hasHcaptchaToken: boolean;
  isResend: boolean;
  phoneNumber: string;
  success: boolean;
}) => {
  const current = attemptFor(phoneNumber);
  current.requests += 1;
  const props = stepProps(context, getPhoneNumberCountry(phoneNumber));
  heapTrackProperties('PhoneVerification:CodeRequested', {
    ...props,
    attemptNumber: current.requests,
    durationMs,
    errorReason: success ? undefined : getVerificationErrorReason(error),
    hasHcaptchaToken,
    isResend,
    secondsSinceCodeSent: isResend ? secondsSince(current.codeSentAt) : undefined,
    success,
  });
  if (success) {
    current.codeSentAt = Date.now();
    current.verifications = 0;
    // Lets every later event this session be split by the phone's country.
    heapAddEventProperties({
      isInternationalPhone: props.isInternational,
      phoneCountry: props.country,
    });
  }
};

export const trackVerifyStepViewed = (context: StepContext & { phoneNumber: string }) => {
  heapTrackProperties('PhoneVerification:VerifyStep:Viewed', {
    ...stepProps(context, getPhoneNumberCountry(context.phoneNumber)),
    secondsSinceCodeSent: secondsSince(attemptFor(context.phoneNumber).codeSentAt),
  });
};

export const trackResendClicked = (
  context: StepContext & { captchaRequired: boolean; phoneNumber: string },
) => {
  const current = attemptFor(context.phoneNumber);
  current.resends += 1;
  heapTrackProperties('PhoneVerification:ResendClicked', {
    ...stepProps(context, getPhoneNumberCountry(context.phoneNumber)),
    captchaRequired: context.captchaRequired,
    resendNumber: current.resends,
    secondsSinceCodeSent: secondsSince(current.codeSentAt),
  });
};

export const trackCodeSubmitted = ({
  error,
  isNewAccount,
  phoneNumber,
  success,
  ...context
}: StepContext & {
  error?: string;
  isNewAccount?: boolean;
  phoneNumber: string;
  success: boolean;
}) => {
  const current = attemptFor(phoneNumber);
  current.verifications += 1;
  heapTrackProperties('PhoneVerification:CodeSubmitted', {
    ...stepProps(context, getPhoneNumberCountry(phoneNumber)),
    attemptNumber: current.verifications,
    errorReason: success ? undefined : getVerificationErrorReason(error),
    isNewAccount,
    secondsSinceCodeSent: secondsSince(current.codeSentAt),
    success,
  });
};

export const trackCaptchaShown = (context: CaptchaContext) => {
  heapTrackProperties('PhoneVerification:Captcha:Shown', captchaProps(context));
};

export const trackCaptchaSolved = (context: CaptchaContext & { secondsToSolve?: number }) => {
  heapTrackProperties('PhoneVerification:Captcha:Solved', {
    ...captchaProps(context),
    secondsToSolve: context.secondsToSolve,
  });
};

export const trackCaptchaExpired = (context: CaptchaContext) => {
  heapTrackProperties('PhoneVerification:Captcha:Expired', captchaProps(context));
};

/** The widget failed, e.g. its script was blocked (`script-error`) or `network-error`. */
export const trackCaptchaError = (context: CaptchaContext & { error: string }) => {
  heapTrackProperties('PhoneVerification:Captcha:Error', {
    ...captchaProps(context),
    error: context.error,
  });
};

export const trackCaptchaRetryClicked = (context: CaptchaContext) => {
  heapTrackProperties('PhoneVerification:Captcha:RetryClicked', captchaProps(context));
};

/** The captcha went away unsolved: the user left the screen or switched to a +1 country. */
export const trackCaptchaAbandoned = (context: CaptchaContext & { secondsShown?: number }) => {
  heapTrackProperties('PhoneVerification:Captcha:Abandoned', {
    ...captchaProps(context),
    secondsShown: context.secondsShown,
  });
};

/** A captcha was needed but this build has no site key, so the user can't get past the gate. */
export const trackCaptchaUnavailable = (context: CaptchaContext) => {
  heapTrackProperties('PhoneVerification:Captcha:Unavailable', {
    ...captchaProps(context),
    reason: 'MissingSiteKey',
  });
};
